import React, {useEffect} from "react";
import {Platform, StyleSheet} from 'react-native';
import {RecoilRoot} from "recoil";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {extendTheme, NativeBaseProvider} from "native-base";
import AppStack from "./src/app-navigation";
import "react-native-gesture-handler";
import Splash from "./src/splash";
import SplashStack from "./src/app-navigation";
import IdleGuard from "./src/IdleGuard";
import BackGuard from "./src/BackGuard";
import {useResolvedFontFamily} from "native-base/lib/typescript/hooks/useResolvedFontFamily";
import {useFonts} from "expo-font";
import FlashMessage from "react-native-flash-message";

/** Customized Theme **/
const theme = extendTheme({
        colors: {
            primary: {
                900: "#db146d",
                800: "#e82e80",
            },
            light: {
                900: "#434343",
                800: "#bcaf9a",
                700: "#dbd3c6",
                600: "#eae3d9",
                500: "#f7f5f2",
            },
            gray: {
                900: "#222222",
                800: "#555555",
                700: "#434343",
                600: "#999999",
                500: "#cccccc",
            },
        },
        fontConfig: {
            NotoSans: {
                900: {
                    normal: "NotoSans-Black",
                },
                700: {
                    normal: "NotoSans-Bold",
                },
                500: {
                    normal: "NotoSans-Regular",
                },
                400: {
                    normal: "NotoSans-Regular",
                },
                300: {
                    normal: "NotoSans-Regular",
                },
                200: {
                    normal: "NotoSans-Regular",
                },
                100: {
                    normal: "NotoSans-Regular",
                },
            },
        },
        fonts: {
            heading: "NotoSans",
            body: "NotoSans",
            mono: "NotoSans",
        },
        config: {
            initialColorMode: "white",
        },
    })
;

export default function App() {
    const queryClient = new QueryClient();

    const [fontsLoaded] = useFonts({
        "NotoSans-Black": require("./assets/fonts/NotoSans-Black.ttf"),
        "NotoSans-Bold": require("./assets/fonts/NotoSans-Bold.ttf"),
        "NotoSans-Regular": require("./assets/fonts/NotoSans-Regular.ttf"),
    });

    // 뷰포트 잠금 + 제스처 억제(Chromium). 브라우저 back-guard는 더 이상 여기서 히스토리를 조작하지 않고
    // Splash(랜딩)의 사용자-탭 real-nav 방식으로 처리한다(삼성 카메라-탭에서 pushState 트랩이 skippable로
    // 무력화됐던 근본 원인 제거). 참고: docs/superpowers/specs/2026-07-11-splash-as-landing-back-guard-design.md
    useEffect(() => {
        if (Platform.OS !== "web") return;

        // 스와이프 back/forward(가로) + 당겨서 새로고침(세로) 제스처 억제(Chromium 계열).
        const de: any = document.documentElement.style, bd: any = document.body.style;
        de.overscrollBehavior = "none"; bd.overscrollBehavior = "none";
        de.overscrollBehaviorX = "none"; bd.overscrollBehaviorX = "none";
        de.overscrollBehaviorY = "none"; bd.overscrollBehaviorY = "none";

        // 앱은 전체화면(각 화면이 내부 FlatList/ScrollView로 스크롤)이라 body 자체 스크롤은 불필요.
        // Expo 웹 기본값(body overflow-y:auto + html height:calc(100% + safe-area-inset))은 페이지를
        // 뷰포트보다 살짝 크게 만들어 body가 스크롤 가능해진다. 그 상태에서 카트 시트 핸들을 위로 드래그하면
        // (핸들 PanResponder는 아래로만 캡처) 제스처가 브라우저로 새어 body가 스크롤 → 모바일 하단 툴바가
        // 숨겨지고 앱 루트 아래로 흰 body 배경이 드러난다. html/body/#root을 뷰포트에 고정·스크롤 잠가 근본 차단.
        de.height = "100%"; bd.height = "100%";
        de.overflow = "hidden"; bd.overflow = "hidden";
        const rootEl = document.getElementById("root");
        if (rootEl) { rootEl.style.height = "100%"; rootEl.style.overflow = "hidden"; }
    }, []);

    if (!fontsLoaded) {
        return null;
    }

    return (
        <RecoilRoot>
            <QueryClientProvider client={queryClient}>
                <NativeBaseProvider theme={theme}>
                    <SplashStack/>
                    <IdleGuard/>
                    <BackGuard/>
                    <FlashMessage position="center" backgroundColor={'black'} />
                </NativeBaseProvider>
            </QueryClientProvider>
        </RecoilRoot>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#fff',
        alignItems: 'center',
        justifyContent: 'center',
    },
});
